import React, { useState, useEffect } from 'react';
import './css/DownloadAppSection.css';

const DownloadAppSection = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        // Check if app is already installed
        if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
            setIsInstalled(true);
        }

        // Listen for the beforeinstallprompt event
        const handleBeforeInstallPrompt = (e) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            // Stash the event so it can be triggered later
            setDeferredPrompt(e);
            setIsInstallable(true);
        };

        // Listen for the appinstalled event
        const handleAppInstalled = () => {
            setIsInstalled(true);
            setIsInstallable(false);
            setDeferredPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
            console.log('User accepted the install prompt');
        } else {
            console.log('User dismissed the install prompt');
        }

        // Clear the deferredPrompt
        setDeferredPrompt(null);
        setIsInstallable(false);
    };

    const handleDownloadClick = () => {
        // For browsers that don't support PWA installation
        // We can provide a link to the app or show instructions
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /Android/.test(navigator.userAgent);

        if (isIOS) {
            alert('To install Agricverse on iOS:\n1. Tap the Share button\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm');
        } else if (isAndroid) {
            alert('To install Agricverse on Android:\n1. Tap the menu button (three dots)\n2. Select "Add to Home Screen" or "Install App"\n3. Follow the prompts to install');
        } else {
            alert('To install Agricverse:\n1. Look for the install icon in your browser\'s address bar\n2. Click it and follow the installation prompts\n3. Or use your browser\'s menu to "Install App"');
        }
    };

    return (
        <section className="download-app-section">
            <div className="download-container">
                <div className="download-content">
                    <div className="download-text">
                        <h2 className="download-title">
                            Download Agricverse App
                        </h2>
                        <p className="download-description">
                            Get the complete farm management experience on your device.
                            Access all features and manage
                            your farm from anywhere with our Agricverse app.
                        </p>


                        <div className="download-buttons">
                            {isInstalled ? (
                                <button className="download-btn installed" disabled>
                                    <i className="fas fa-check-circle"></i>
                                    App Installed
                                </button>
                            ) : isInstallable ? (
                                <button className="download-btn install" onClick={handleInstallClick}>
                                    <i className="fas fa-download"></i>
                                    Install App
                                </button>
                            ) : (
                                <button className="download-btn download" onClick={handleDownloadClick}>
                                    <i className="fas fa-download"></i>
                                    Download App
                                </button>
                            )}

                            <button className="download-btn secondary" onClick={() => window.open('/about', '_blank')}>
                                <i className="fas fa-info-circle"></i>
                                Learn More
                            </button>
                        </div>
                    </div>

                    <div className="download-visual">
                        <div className="phone-mockup">
                            <div className="phone-screen">
                                <div className="app-preview">
                                    <div className="app-header">
                                        <img
                                            src="/AGRICVERSE_DARK.png"
                                            alt="AgricVerse Logo"
                                            style={{ height: 50, cursor: "pointer" }}
                                        />
                                    </div>
                                    <div className="app-content">
                                        <div className="preview-item">
                                            <i className="fas fa-chart-line"></i>
                                            <span>Farm Analytics</span>
                                        </div>
                                        <div className="preview-item">
                                            <i className="fas fa-robot"></i>
                                            <span>AI Assistant</span>
                                        </div>
                                        <div className="preview-item">
                                            <i className="fas fa-users"></i>
                                            <span>Expert Network</span>
                                        </div>
                                        <div className="preview-item">
                                            <i className="fas fa-shopping-cart"></i>
                                            <span>Marketplace</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="floating-elements">
                            <div className="floating-icon icon-1">
                                <i className="fas fa-leaf"></i>
                            </div>
                            <div className="floating-icon icon-2">
                                <i className="fas fa-tractor"></i>
                            </div>
                            <div className="floating-icon icon-3">
                                <i className="fas fa-seedling"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DownloadAppSection;
